import React, { useState } from 'react';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Button } from '@zurich-primitives/button';
import {
  getBootstrapApp,
  getHomeAccountId,
  getUserName,
  setBusinessArea,
  setCurrentFile,
  setCurrentSupportPath,
  setEmailChain,
  setService,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import { handleLogoutMsal } from 'app/auth';
import { getInitials, smoothScrollingToAnchor } from 'app/utils';
import { ReactComponent as GenAILogo } from 'app/assets/GenAILogo.svg';
import { ReactComponent as Logout } from 'app/assets/Icons/Logout.svg';
import styles from './TopNav.module.scss';
import config from 'config';

export const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const path = window.location.pathname;
  const username = useAppSelector(getUserName);
  const hmeAccountId = useAppSelector(getHomeAccountId);
  const [showDropdown, setShowDropdown] = useState(false);
  const { features } = useAppSelector(getBootstrapApp);

  const resetSelections = (): void => {
    dispatch(setBusinessArea(undefined));
    dispatch(setService(undefined));
    dispatch(setCurrentFile(undefined));
    dispatch(setEmailChain(undefined));
    dispatch(setCurrentSupportPath('something-missing'));
  };

  const handleLogoNavigation = (): void => {
    if (path === config.APP.PATH.LANDING) {
      window.location = path as string & Location;
    } else {
      navigate(config.APP.PATH.LANDING);
    }
    resetSelections();
  };

  const navLanding = (anchor: string): void => {
    resetSelections();
    path === config.APP.PATH.LANDING
      ? smoothScrollingToAnchor(anchor)
      : navigate(`${config.APP.PATH.LANDING}?anchor=${anchor}`);
  };

  const handleNavigation = (path: string): void => {
    resetSelections();
    navigate(path);
  };

  const handleMouseEnter = (): void => {
    setShowDropdown(true);
  };

  const handleMouseLeave = (): void => {
    setShowDropdown(false);
  };

  return (
    <div className={styles.topbar}>
      <div className={styles.wrapper}>
        <div
          role="button"
          className={styles.logo}
          onClick={handleLogoNavigation}
          tabIndex={0}
        >
          <GenAILogo />
          GenAI Business Services
        </div>
        <nav>
          <ul>
            <li>
              <button
                className={styles['nav-item']}
                onClick={() => navLanding('businessareas')}
              >
                Business Area
              </button>
            </li>
            <li>
              <button
                className={styles['nav-item']}
                onClick={() => navLanding('services')}
              >
                Services
              </button>
            </li>
            <li
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className={styles['nav-item-dropdown']}
            >
              <button className={styles['nav-item']} disabled={!features.costs}>
                Costs
              </button>

              {showDropdown && features.costs && (
                <ul className={styles['dropdown-menu']}>
                  <li>
                    <button
                      className={styles['dropdown-item']}
                      onClick={() =>
                        handleNavigation(config.APP.PATH.COST_ESTIMATOR)
                      }
                    >
                      Estimator
                    </button>
                  </li>
                  <li>
                    <button
                      className={styles['dropdown-item']}
                      onClick={() =>
                        handleNavigation(config.APP.PATH.COST_METERING)
                      }
                    >
                      Metering
                    </button>
                  </li>
                </ul>
              )}
            </li>

            <li>
              <button className={styles['nav-item']} disabled>
                Guidelines
              </button>
            </li>
            <li>
              <button
                className={styles['nav-item']}
                onClick={() => handleNavigation(config.APP.PATH.SUPPORT)}
              >
                Feedback
              </button>
            </li>
          </ul>
          <Button
            className={classnames(styles['nav-item'], styles.logout)}
            onClick={() => handleLogoutMsal(hmeAccountId)}
            aria-label="Logout"
          >
            <Button.Label>{username && getInitials(username)}</Button.Label>
            <Button.Icon>
              <Logout width="1.5rem" height="1.5rem" />
            </Button.Icon>
          </Button>
        </nav>
      </div>
    </div>
  );
};
